import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button, Flex, Tooltip } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import { createErrorToast, createSuccessToast } from '../../../redux/modules/app';
import { showModal } from '../../../redux/modules/modal';
import { downloadImportReport, useImportAction } from '../../../services/import-service';

import RollbackImportModal from './RollbackImportModal';
import { availableActions, hasManifest } from './util';

const ACTION_LABELS = {
  stageFromDropbox: 'Pull from Dropbox',
  cancelPull: 'Cancel pull',
  validate: 'Validate (dry run)',
  run: 'Run',
  pause: 'Pause',
  resume: 'Resume'
};

const ACTION_TOASTS = {
  stageFromDropbox: 'Pulling the Dropbox folder into the import',
  cancelPull: 'Dropbox pull cancelled; pull again to resume where it stopped',
  validate: 'Dry run started',
  run: 'Import started',
  pause: 'Import will pause after the current row',
  resume: 'Import resumed'
};

/**
 * The state machine's buttons: only the transitions the API would accept are
 * offered, in the order support takes them. `only` narrows them to the ones a
 * wizard step owns.
 */
const ImportActions = ({ importData, only = null }) => {
  const dispatch = useDispatch();
  const [pending, setPending] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const { mutateAsync: act } = useImportAction(importData.id);

  const offered = availableActions(importData);
  const wanted = action => !only || only.includes(action);
  const actions = Object.fromEntries(Object.entries(offered).map(([action, available]) => [action, available && wanted(action)]));

  const perform = async action => {
    setPending(action);

    try {
      await act(action);
      dispatch(createSuccessToast({ title: ACTION_TOASTS[action] }));
    } catch (error) {
      dispatch(createErrorToast({ title: `Could not ${ACTION_LABELS[action].toLowerCase()}`, description: error?.message, error }));
    } finally {
      setPending(null);
    }
  };

  const handleRollback = () => {
    dispatch(showModal(RollbackImportModal, { title: 'Roll back this import', size: 'lg' }, { importId: importData.id }));
  };

  const handleReport = async () => {
    setDownloading(true);

    try {
      await downloadImportReport(importData.id);
    } catch (error) {
      dispatch(createErrorToast({ title: 'Could not download the issues report', description: error?.message, error }));
    } finally {
      setDownloading(false);
    }
  };

  const button = (action, variant = 'primary') => actions[action] && (
    <Button
      data-testid={`import-action-${action}`}
      isDisabled={pending !== null && pending !== action}
      isLoading={pending === action}
      key={action}
      variant={variant}
      onClick={() => perform(action)}
    >
      {ACTION_LABELS[action]}
    </Button>
  );

  const validateHint = wanted('validate') && !hasManifest(importData) ? 'Upload a manifest first' : null;

  return (
    <Flex alignItems="center" flexWrap="wrap" gap={3}>
      {button('stageFromDropbox', 'secondary')}
      {button('cancelPull', 'secondary')}
      {validateHint && !actions.validate && !actions.run && !actions.pause && !actions.resume ? (
        <Tooltip label={validateHint}>
          <Button isDisabled data-testid="import-action-validate" variant="primary">{ACTION_LABELS.validate}</Button>
        </Tooltip>
      ) : button('validate', actions.run ? 'secondary' : 'primary')}
      {button('run')}
      {button('pause', 'secondary')}
      {button('resume')}
      {actions.rollback && (
        <Button data-testid="import-action-rollback" variant="secondary" onClick={handleRollback}>Roll back…</Button>
      )}
      {actions.report && (
        <Button data-testid="import-action-report" isLoading={downloading} variant="textGrey" onClick={handleReport}>
          Download issues CSV
        </Button>
      )}
    </Flex>
  );
};

ImportActions.propTypes = {
  importData: PropTypes.shape({
    id: PropTypes.number.isRequired,
    status: PropTypes.string,
    source: PropTypes.string,
    manifestS3Key: PropTypes.string,
    rowCounts: PropTypes.object,
    summary: PropTypes.object
  }).isRequired,
  only: PropTypes.arrayOf(PropTypes.string)
};

export default ImportActions;
